import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { Plan, PlanFeature } from '../types';
import Modal from '../components/Modal';
import PlanFeatureForm from '../components/PlanFeatureForm';

const PlanDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [plan, setPlan] = useState<Plan | null>(null);
  const [features, setFeatures] = useState<PlanFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState<PlanFeature | null>(null);

  useEffect(() => {
    if (id) {
      fetchPlanDetails();
      fetchPlanFeatures();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPlanDetails = async () => {
    try {
      const response = await apiService.getPlan(Number(id));
      setPlan(response);
    } catch (error) {
      console.error('Error fetching plan details:', error);
    }
  };

  const fetchPlanFeatures = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPlanFeatures(Number(id));
      setFeatures(response);
    } catch (error) {
      console.error('Error fetching plan features:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFeature = () => {
    setEditingFeature(null);
    setShowFeatureModal(true);
  };

  const handleEditFeature = (feature: PlanFeature) => {
    setEditingFeature(feature);
    setShowFeatureModal(true);
  };

  const getFeatureTypeColor = (type: string) => {
    switch (type) {
      case 'FEATURE':
        return 'badge-info';
      case 'DOCUMENT_ACCESS':
        return 'badge-success';
      case 'AUDIT_ACCESS':
        return 'badge-warning';
      default:
        return 'badge-secondary';
    }
  };

  const getFeatureTypeLabel = (type: string) => {
    switch (type) {
      case 'FEATURE':
        return 'Feature';
      case 'DOCUMENT_ACCESS':
        return 'Document Access';
      case 'AUDIT_ACCESS':
        return 'Audit Access';
      default:
        return type;
    }
  };

  const groupedFeatures = features.reduce((acc, feature) => {
    if (!acc[feature.feature_type]) {
      acc[feature.feature_type] = [];
    }
    acc[feature.feature_type].push(feature);
    return acc;
  }, {} as Record<string, PlanFeature[]>);

  if (loading && !plan) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="page-header">
        <h1>Plan not found</h1>
        <Link to="/plans" className="btn btn-secondary">
          Back to Plans
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>{plan.name}</h1>
          <p style={{ color: '#7f8c8d', margin: '5px 0 0 0' }}>
            Plan ID: {plan.plan_id}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-primary" onClick={handleCreateFeature}>
            + Add Feature
          </button>
          <Link to="/plans" className="btn btn-secondary">
            Back to Plans
          </Link>
        </div>
      </div>

      {/* Plan Information */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Plan Information</h3>
          <span className={`badge ${plan.is_active ? 'badge-success' : 'badge-danger'}`}>
            {plan.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div>
            <strong>Description:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {plan.description || 'No description provided'}
            </p>
          </div>
          <div>
            <strong>Valid From:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {new Date(plan.valid_from).toLocaleDateString()}
            </p>
          </div>
          <div>
            <strong>Valid To:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {plan.valid_to ? new Date(plan.valid_to).toLocaleDateString() : 'No end date'}
            </p>
          </div>
          <div>
            <strong>Created:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {new Date(plan.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Features by Type */}
      {Object.entries(groupedFeatures).map(([featureType, typeFeatures]) => (
        <div key={featureType} className="card">
          <div className="card-header">
            <h3 className="card-title">
              {getFeatureTypeLabel(featureType)} ({typeFeatures.length})
            </h3>
            <span className={`badge ${getFeatureTypeColor(featureType)}`}>
              {getFeatureTypeLabel(featureType)}
            </span>
          </div>
          <div>
            {typeFeatures.length === 0 ? (
              <p style={{ color: '#7f8c8d', textAlign: 'center', padding: '20px' }}>
                No {getFeatureTypeLabel(featureType).toLowerCase()} features found
              </p>
            ) : (
              <ul className="feature-list">
                {typeFeatures.map((feature) => (
                  <li key={feature.feature_id} className="feature-item">
                    <div className="feature-info">
                      <div className="feature-name">{feature.name}</div>
                      {feature.description && (
                        <div className="feature-description">{feature.description}</div>
                      )}
                      {feature.trial_period_days > 0 && (
                        <div className="feature-description">
                          Trial period: {feature.trial_period_days} days
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span className={`badge ${feature.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {feature.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleEditFeature(feature)}
                      >
                        Edit
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ))}

      {/* Empty state if no features */}
      {features.length === 0 && (
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h3 style={{ color: '#7f8c8d', marginBottom: '10px' }}>No Features Yet</h3>
            <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
              This plan doesn't have any features, document access, or audit access configured.
            </p>
            <button className="btn btn-primary" onClick={handleCreateFeature}>
              Add First Feature
            </button>
          </div>
        </div>
      )}

      {showFeatureModal && (
        <Modal
          title={editingFeature ? 'Edit Feature' : 'Add Feature'}
          onClose={() => setShowFeatureModal(false)}
        >
          <PlanFeatureForm
            planId={Number(id)}
            initialData={editingFeature}
            onSubmit={() => {
              setShowFeatureModal(false);
              fetchPlanFeatures();
            }}
            onCancel={() => setShowFeatureModal(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default PlanDetails;